import React from "react";
import {
  Input,
  Form,
  Switch,
  Select,
  InputNumber,
  Upload,
  message,
  DatePicker,
  TreeSelect,
} from "antd";
import { useController, useForm } from "react-hook-form";
import { PlusOutlined } from "@ant-design/icons";
import moment from "moment";

export const AntdInput = ({ control, name, ...v }) => {
  const { field } = useController({ name, control });
  return (
    <Input
      type="text"
      value={field.value}
      onChange={(e) => {
        field.onChange(e.target.value);
      }}
      {...v}
    />
  );
};

export const AntdSwitch = ({ control, name, onChange, ...v }) => {
  const { field } = useController({ name, control });
  const switchChange = (e) => field.onChange(e);
  return <Switch onChange={onChange || switchChange} checked={field.value} {...v} />;
};

export const AntdTextArea = ({ control, name, ...v }) => {
  const { field } = useController({ name, control });
  return (
    <Input.TextArea
      value={field.value}
      onChange={(e) => {
        field.onChange(e.target.value);
      }}
      {...v}
    />
  );
};

export const AntdDatePicker = ({ control, name, onChange, ...v }) => {
  const { field } = useController({ name, control });
  const itemProps = {
    ...(field.value && { value: moment(field.value, "YYYY-MM-DD") }),
  };
  return (
    <DatePicker
      style={{ width: "100%" }}
      onChange={(e, v) => {
        field.onChange(v);
      }}
      {...itemProps}
    />
  );
};

export const AntdInputSelect = ({ control, name, onChange, ...v }) => {
  const { field } = useController({ name, control });
  const mode = v.mode;
  const selectChange = (v, e) => {
    if (v === undefined) {
      field.onChange("");
      return;
    }
    if (mode === "multiple") field.onChange(e);
    else field.onChange(v);
  };
  return (
    <Select
      filterOption={(input, option) =>
        (option.label ?? "").toLowerCase().includes(input.toLowerCase())
      }
      showSearch
      value={field.value}
      allowClear={true}
      onChange={onChange || selectChange}
      {...v}
    />
  );
};

export const AntdFormItem = ({ children, label, labelCol, errors, required }) => {
  const itemProps = {
    ...(labelCol && { labelCol }),
    ...(label && { label }),
    ...(required && { required }),
    ...(errors && { validateStatus: "error", help: errors.message }),
  };
  return (
    <Form.Item style={{ marginBottom: "10px" }} {...itemProps}>
      {children}
    </Form.Item>
  );
};

export const AntdInputNumber = ({ control, name }) => {
  const { field } = useController({ name, control });
  return (
    <InputNumber
      style={{ width: "100%" }}
      min={0}
      value={field.value}
      onChange={(e) => {
        field.onChange(e);
      }}
    />
  );
};

export const AntdUpload = ({ control, name, ...v }) => {
  const { field } = useController({ name, control });
  const checkResolution = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const { width, height } = img;
        const min = 330;
        const max = 2048;
        const isValid = width >= 330 && height >= 330;
        // const isValid = width >= 330 && height >= 330 && width <= 2048 && height <= 2048;
        URL.revokeObjectURL(img.src); // 释放内存

        if (!isValid) {
          message.error(
            `Image dimensions must be between ${min}px and ${max}px. Current: ${width}x${height}px`
          );
          reject();
        } else {
          resolve();
        }
      };
    });
  };
  const handleBeforeUpload = async (file) => {
    // 2. 校验分辨率
    try {
      await checkResolution(file);
      return false; // 返回 false 表示手动处理上传，不自动上传到服务器
    } catch (e) {
      return Upload.LIST_IGNORE; // 校验失败，不加入列表
    }
  };
  return (
    <Upload
      listType="picture-card"
      accept=".jpg,.jpeg,.png"
      beforeUpload={handleBeforeUpload}
      fileList={field.value}
      {...v}
      onChange={(info) => {
        const isValid = info.fileList.some((x) => {
          return x.size / 1024 / 1024 > 1;
        });
        if (isValid) {
          message.error("image size cannot greater than 1mb");
          return;
        }
        if (info.fileList.length > 8) {
          message.error("maximum can upload 8 image");
          return;
        }
        field.onChange(info.fileList);
      }}
    >
      {field.value >= 8 ? null : <PlusOutlined />}
    </Upload>
  );
};

export const AntdTreeSelect = ({ control, name, ...v }) => {
  const { field } = useController({ name, control });
  return (
    <TreeSelect
      placeholder="Search Categories"
      style={{ width: "100%" }}
      value={field.value}
      showSearch
      multiple
      treeCheckable
      treeCheckStrictly
      treeNodeFilterProp="label"
      // filterTreeNode={(search, item) => {
      //   return item.label.toLowerCase().indexOf(search.toLowerCase()) >= 0;
      // }}
      onChange={(v) => {
        field.onChange(v.map((x) => ({ label: x.label, value: x.value })));
      }}
      {...v}
    />
  );
};
